import Component from '@cx-modules/offers-slim';
import offersSlimCss from '@cx-modules/offers-slim/style.css?inline';
import { Analytics } from '../../../../services/analytics.js';
import { render } from '../../../../services/cx-module-helper';
import createI18nInstance from '../../../../services/i18n/index.js';
import ThemeService from '../../../../services/theme';
import { createResizeListener } from '../../../../utils/events/resize.js';
import { checkIfAnalyticsIsDisabled } from '../../../../utils/isAnalyticsDisabled/isAnalyticsDisabled.js';
import { isPlainObject } from '../../../../utils/isPlainObject/isPlainObject.js';
import { html } from '../../../../utils/lit/html.js';
import { upgradeProperty } from '../../../../utils/upgradeProperty/upgradeProperty.js';
import { wrapperEvent } from '../../../oc-connect-widget/eventEmitter.js';
import { clickoutTypes } from '../../shared/utils.js';
import { createDehydratedState } from './state.js';
import themeContract from './themes/contract.js';

const WIDGET_NAME = 'oc-offers-widget-variation-horizontal-slim';
const template = document.createElement('template');

import themes from './themes/index.js';

const wEvent = (name, detail) =>
  new CustomEvent(`${WIDGET_NAME}:${name}`, {
    bubbles: true,
    composed: true,
    detail
  });

template.innerHTML = html`
  <style>
    * {
      box-sizing: inherit;
    }
  </style>
  <div id="rootElement"></div>
  <slot name="loading" hidden> Loading... </slot>
  <slot name="error" hidden></slot>
`;

class OCOffersWidgetVariationHorizontalSlim extends HTMLElement {
  #initialData = null;

  constructor() {
    super();

    if (!this.shadowRoot) {
      this.attachShadow({ mode: 'open' });
      this.shadowRoot.appendChild(template.content.cloneNode(true));
    }
  }

  get _initialData() {
    return this.#initialData;
  }

  // NOTE `_initialData` should not be used manually.
  // Its purpose is to be used by the host element to provide the initial data to the child component.
  set _initialData(value) {
    if (isPlainObject(value)) {
      this.#initialData = value;
    }
  }

  fireResizeEvent = (data, eventOrigin) => {
    switch (eventOrigin) {
      case 'widget': {
        return this.dispatchEvent(wEvent('resize', data));
      }
      case 'wrapper': {
        return this.dispatchEvent(wrapperEvent('resize', data));
      }

      default: {
        return;
      }
    }
  };

  async connectedCallback() {
    upgradeProperty(this, '_initialData');

    const data = this._initialData;

    if (!data || !Array.isArray(data.offers) || !data.offers.length) {
      return;
    }

    this.injectCxModule(data);
  }

  async injectCxModule(data) {
    let resizeObserverCleanupFn;
    try {
      const getRootElement = () =>
        this.shadowRoot.getElementById('rootElement');
      const i18n = data?.repub ? await createI18nInstance(data.repub) : null;
      const target = getRootElement();

      if (!Component || !target) {
        return;
      }

      const placeholders = this.getAttribute('placeholders');
      const mutatedData = { apiKey: 'NO_API_KEY', ...data };
      const variant = this.hasAttribute('autohide-terms')
        ? 'autohide'
        : 'default';

      const cxProps = {
        apiKey: mutatedData.apiKey,
        dehydratedState: createDehydratedState(
          mutatedData,
          'offers-slim',
          data.idForCxCache,
          mutatedData.apiKey
        ),
        i18n,
        placeholders: this.getAttribute('placeholders'),
        variant,
        vcId: data.idForCxCache,
        clickoutService: clickoutTypes.LEGACY
      };

      if (data.variantType === 'slot') {
        cxProps.clickoutServiceUrl =
          import.meta.env.VITE_OFFERS_CLICKOUT_SERVICE_URL;
        cxProps.clickoutService = data?.clickoutService
          ? clickoutTypes.NEW
          : clickoutTypes.LEGACY;
        cxProps.gtmId = data.gtmId;
        cxProps.recommendedMarketId = data?.recommendedMarketId;
        cxProps.pageUrl = window.location.pathname;
        cxProps.fullPageUrl = window.location.href;
      }

      if (placeholders) cxProps.placeholders = placeholders;

      if (
        !checkIfAnalyticsIsDisabled(
          data.trackingRate,
          this.hasAttribute('disable-analytics')
        )
      ) {
        this.analytics = new Analytics(
          data.gtmId,
          data.useDefaultDataLayer ? 'dataLayer' : undefined
        );
        this.analytics.integrateAnalytics();

        cxProps.analyticsDataLayer = this.analytics.getDataLayer();

        const {
          recommendedMarketId,
          hash: widgetSlot,
          campaignName
        } = data || {};

        cxProps.analyticsImpressionEventData = {
          ...(widgetSlot &&
            campaignName && {
              widgetSlot,
              campaignName
            }),
          ...(recommendedMarketId ? { recommendedMarketId } : {})
        };
      } else {
        cxProps.disableAnalytics = true;
      }

      const { css } = ThemeService({
        campaignData: data,
        el: this,
        applyDefaultTheme: false,
        themesFn: themes,
        themeContract
      });

      const style = document.createElement('style');
      style.textContent = offersSlimCss;
      this.shadowRoot.appendChild(style);

      const themeStyle = document.createElement('style');
      themeStyle.textContent = css;

      if (this.hasAttribute('z-index-patch')) {
        const zIndexVal = this.getAttribute('z-index-patch');
        const isLiteral = [
          'unset',
          'initial',
          'auto',
          'inherit',
          'revert',
          'revert-layer'
        ];
        const zIndexHostCss = `:host {
          position: relative;
          z-index: ${isLiteral.includes(zIndexVal) ? zIndexVal : Number(zIndexVal ?? 1)};
        }`;

        themeStyle.textContent += zIndexHostCss;
      }
      this.shadowRoot.appendChild(themeStyle);

      await render(target, Component, cxProps);
      this.dispatchEvent(wEvent('load-success', data));
      resizeObserverCleanupFn = createResizeListener({
        selectorFn: getRootElement,
        fireEventFn: this.fireResizeEvent,
        hasConnectWrapper: this.getAttribute('hasConnectWrapper')
      });
    } catch (error) {
      this.dispatchEvent(wEvent('load-error', { error }));
      resizeObserverCleanupFn?.();
    } finally {
      this.dispatchEvent(wEvent('load'));
    }
  }
}

if (window.customElements && !window.customElements.get(WIDGET_NAME)) {
  window.customElements.define(
    WIDGET_NAME,
    OCOffersWidgetVariationHorizontalSlim
  );
}
